import { isPlatformBrowser, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, inject, isDevMode, PLATFORM_ID, Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@spartacus/core';
import { LoggerService, Config, provideDefaultConfig } from '@spartacus/core';
import { Subscription, fromEvent, EMPTY, of } from 'rxjs';
import { switchMap, map, filter, tap } from 'rxjs/operators';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let QualtricsEmbeddedFeedbackComponent = /*#__PURE__*/(() => {
  class QualtricsEmbeddedFeedbackComponent {
    static {
      this.ɵfac = function QualtricsEmbeddedFeedbackComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QualtricsEmbeddedFeedbackComponent)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: QualtricsEmbeddedFeedbackComponent,
        selectors: [["cx-qualtrics-embedded-feedback"]],
        standalone: false,
        decls: 0,
        vars: 0,
        template: function QualtricsEmbeddedFeedbackComponent_Template(rf, ctx) {},
        encapsulation: 2
      });
    }
  }
  return QualtricsEmbeddedFeedbackComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultQualtricsConfig = {
  qualtrics: {}
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const QUALTRICS_EVENT_NAME = 'qsi_js_loaded';
/**
 * Service to integration Qualtrics.
 *
 * The integration observes the Qualtrics API, and when available, it runs the QSI API
 * to let Qualtrics evaluate the application.
 *
 * The service supports an additional _hook_ (`isDataLoaded()`) that can be used to load application
 * data before pulling the QSI API. This is beneficial in a single page application when additional
 * data is required before the Qualtrics _creatives_ run.
 *
 * This service also supports the creation of the Qualtrics deployment script. This is optional, as
 * the script can be added in alternatives ways.
 */
let QualtricsLoaderService = /*#__PURE__*/(() => {
  class QualtricsLoaderService {
    get window() {
      return this.winRef.nativeWindow;
    }
    constructor(winRef, platformId, scriptLoader) {
      this.winRef = winRef;
      this.platformId = platformId;
      this.scriptLoader = scriptLoader;
      this.subscription = new Subscription();
      this.logger = inject(LoggerService);
      /**
       * QSI load event that happens when the QSI JS file is loaded.
       */
      this.qsiLoaded$ = isPlatformBrowser(this.platformId) && this.window ? fromEvent(this.window, QUALTRICS_EVENT_NAME) : EMPTY;
      /**
       * Emits the Qualtrics Site Intercept (QSI) JavaScript API whenever available.
       *
       * The API is emitted when the JavaScript resource holding this API is fully loaded.
       * The API is also stored locally in the service, in case it's required later on.
       */
      this.qsi$ = this.qsiLoaded$.pipe(switchMap(() => this.isDataLoaded()), map(dataLoaded => dataLoaded ? this.window?.QSI : EMPTY), filter(qsi => Boolean(qsi)), tap(qsi => this.qsiApi = qsi));
      this.initialize();
    }
    /**
     * Adds the deployment script to the DOM.
     *
     * The script will not be added twice if it was loaded before. In that case, we use
     * the Qualtrics API directly to _unload_ and _run_ the project.
     */
    addScript(scriptSource) {
      if (this.hasScript(scriptSource)) {
        this.run(true);
      } else {
        this.scriptLoader.embedScript({
          src: scriptSource
        });
      }
    }
    /**
     * Indicates if the script is already added to the DOM.
     */
    hasScript(source) {
      return !!this.winRef.document.querySelector(`script[src="${source}"]`);
    }
    /**
     * Starts observing the Qualtrics integration. The integration is based on a
     * Qualtrics specific event (`qsi_js_loaded`). As soon as this events happens,
     * we run the API.
     */
    initialize() {
      this.subscription.add(this.qsi$.subscribe(() => this.run()));
    }
    /**
     * Evaluates the Qualtrics project code for the application.
     *
     * In order to reload the evaluation in Qualtrics, the API requires to unload the API before
     * running it again. We don't do this by default, but offer a flag to conditionally unload the API.
     */
    run(reload = false) {
      if (!this.qsiApi?.API) {
        if (isDevMode()) {
          this.logger.log('The QSI api is not available');
        }
        return;
      }
      if (reload) {
        // Removes any currently displaying creatives
        this.qsiApi.API.unload();
      }
      // Starts the intercept code evaluation right after loading the Site Intercept
      // code for any defined intercepts or creatives
      this.qsiApi.API.load().done(this.qsiApi.API.run());
    }
    /**
     * This logic exist in order to let the client(s) add their own logic to wait for any kind of page data.
     * You can observe any data in this method.
     *
     * Defaults to true.
     */
    isDataLoaded() {
      return of(true);
    }
    ngOnDestroy() {
      this.subscription?.unsubscribe();
    }
    static {
      this.ɵfac = function QualtricsLoaderService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QualtricsLoaderService)(i0.ɵɵinject(i1.WindowRef), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(i1.ScriptLoader));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: QualtricsLoaderService,
        factory: QualtricsLoaderService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return QualtricsLoaderService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Configuration options for the Qualtrics integration, which allows you to
 * specify the qualtrics project and deployment script.
 */
let QualtricsConfig = /*#__PURE__*/(() => {
  class QualtricsConfig {
    static {
      this.ɵfac = function QualtricsConfig_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QualtricsConfig)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: QualtricsConfig,
        factory: function QualtricsConfig_Factory(__ngFactoryType__) {
          let __ngConditionalFactory__ = null;
          if (__ngFactoryType__) {
            __ngConditionalFactory__ = new (__ngFactoryType__ || QualtricsConfig)();
          } else {
            __ngConditionalFactory__ = i0.ɵɵinject(Config);
          }
          return __ngConditionalFactory__;
        },
        providedIn: 'root'
      });
    }
  }
  return QualtricsConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Adds the Qualtrics deployment script whenever the component is loaded. The
 * deployment script is loaded from the global configuration (`qualtrics.scriptSource`).
 */
let QualtricsComponent = /*#__PURE__*/(() => {
  class QualtricsComponent {
    constructor(qualtricsLoader, config) {
      this.qualtricsLoader = qualtricsLoader;
      this.config = config;
      this.logger = inject(LoggerService);
      if (this.config.qualtrics?.scriptSource) {
        this.qualtricsLoader.addScript(this.config.qualtrics.scriptSource);
      } else if (isDevMode()) {
        this.logger.warn(`We're unable to add the Qualtrics deployment code as there is no script source defined in config.qualtrics.scriptSource.`);
      }
    }
    static {
      this.ɵfac = function QualtricsComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QualtricsComponent)(i0.ɵɵdirectiveInject(QualtricsLoaderService), i0.ɵɵdirectiveInject(QualtricsConfig));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: QualtricsComponent,
        selectors: [["cx-qualtrics"]],
        standalone: false,
        decls: 0,
        vars: 0,
        template: function QualtricsComponent_Template(rf, ctx) {},
        encapsulation: 2
      });
    }
  }
  return QualtricsComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let QualtricsComponentsModule = /*#__PURE__*/(() => {
  class QualtricsComponentsModule {
    static {
      this.ɵfac = function QualtricsComponentsModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || QualtricsComponentsModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: QualtricsComponentsModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideDefaultConfig({
          cmsComponents: {
            QualtricsEmbeddedFeedbackComponent: {
              component: QualtricsEmbeddedFeedbackComponent
            },
            QualtricsComponent: {
              component: QualtricsComponent
            }
          }
        }), provideDefaultConfig(defaultQualtricsConfig)],
        imports: [CommonModule]
      });
    }
  }
  return QualtricsComponentsModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Generated bundle index. Do not edit.
 */

export { QUALTRICS_EVENT_NAME, QualtricsComponent, QualtricsComponentsModule, QualtricsConfig, QualtricsEmbeddedFeedbackComponent, QualtricsLoaderService };
